import React, { useState, useEffect } from "react";
import "./Leaverequest.css";
import apiClient from "../utils/apiClient";

const LeaveRequests = () => {
  const [requests, setRequests] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(""); // Approved | Rejected
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ================= FETCH LEAVE REQUESTS ================= */
  useEffect(() => {
    fetchLeaveRequests();
  }, []);

  const fetchLeaveRequests = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await apiClient("/leaves/");
      if (!data) return;

      const list = Array.isArray(data.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      // Show only Pending requests
      setRequests(list.filter((r) => r.status === "Pending"));
    } catch (err) {
      console.error(err);
      setError("Failed to fetch leave requests.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= OPEN CONFIRM POPUP ================= */
  const openPopup = (req, action) => {
    setSelectedRequest(req);
    setActionType(action);
    setShowPopup(true);
  };

  /* ================= CONFIRM APPROVE / REJECT ================= */
  const handleConfirm = async () => {
    if (!selectedRequest || !actionType) return;

    try {
      setLoading(true);

      const endpoint =
        actionType === "Approved"
          ? `/leaves/${selectedRequest.id}/approve/`
          : `/leaves/${selectedRequest.id}/reject/`;

      const result = await apiClient(endpoint, {
        method: "POST",
      });

      if (!result) {
        throw new Error("Action failed");
      }

      // Remove processed request from UI
      setRequests((prev) =>
        prev.filter((r) => r.id !== selectedRequest.id)
      );

      setShowPopup(false);
      setSelectedRequest(null);
      setActionType("");
    } catch (err) {
      console.error(err);
      alert(`Failed to ${actionType.toLowerCase()} leave request`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowPopup(false);
    setSelectedRequest(null);
    setActionType("");
  };

  /* ================= UI ================= */
  return (
    <div className="leave-dashboard">
      <h2>Pending Leave Requests</h2>

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <p className="loading-text">Loading leave requests...</p>
      ) : (
        <table className="leave-table3">
          <thead>
            <tr>
              <th>Name</th>
              <th>Department</th>
              <th>Subject</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Leave Type</th>
              <th>Days</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {requests.length > 0 ? (
              requests.map((req) => (
                <tr key={req.id}>
                  <td>{req.employee_name}</td>
                  <td>{req.employee_department}</td>
                  <td>{req.subject}</td>
                  <td>{req.start_date}</td>
                  <td>{req.end_date}</td>
                  <td>{req.leave_type}</td>
                  <td>{req.days}</td>
                  <td>{req.status}</td>
                  <td>
                    <button
                      className="btn-approve"
                      onClick={() => openPopup(req, "Approved")}
                      disabled={loading}
                    >
                      Approve
                    </button>
                    <button
                      className="btn-reject"
                      onClick={() => openPopup(req, "Rejected")}
                      disabled={loading}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9">No pending leave requests.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* ================= CONFIRM MODAL ================= */}
      {showPopup && (
        <div className="modal12-overlay">
          <div className="modal12-content">
            <h3>Confirm {actionType}</h3>

            <p>
              Are you sure you want to{" "}
              <b>{actionType.toLowerCase()}</b> the leave request for{" "}
              <b>{selectedRequest?.employee_name}</b>?
            </p>

            <div className="modal12-buttons">
              <button
                className="btn12-confirm"
                onClick={handleConfirm}
                disabled={loading}
              >
                Yes, {actionType}
              </button>

              <button
                className="btn12-cancel"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRequests;
